import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card, EmptyState } from '@/components/Card';
import { TextInput } from '@/components/Field';
import { TabBar } from '@/components/Tabs';
import { useToast } from '@/components/Toast';
import { assess } from '@/domain/engine';
import { buildExportPayload, exportFileName } from '@/domain/storage';
import type { Project } from '@/domain/types';
import { useIsNarrow } from '@/hooks/useMediaQuery';
import { downloadJson } from '@/lib/download';
import { ParamsPanel } from '@/features/editor/ParamsPanel';
import { ResultPanel } from '@/features/results/ResultPanel';
import { useProjectStore } from '@/app/projectStore';

type TabKey = 'params' | 'results';

export function ProjectPage() {
  const { projectId = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const narrow = useIsNarrow();
  const { getProject, updateProject, setLastProjectId, ready } = useProjectStore();
  const [tab, setTab] = useState<TabKey>('params');

  const project = getProject(projectId);

  // 只在切换到另一个项目时记录「最近打开」；依赖项目对象会让它在每次输入后重跑。
  useEffect(() => {
    if (projectId) setLastProjectId(projectId);
  }, [projectId, setLastProjectId]);

  const assessment = useMemo(() => (project ? assess(project) : null), [project]);

  if (!ready) return null;

  if (!project || !assessment) {
    return (
      <Card>
        <EmptyState
          title="找不到这个项目"
          description="项目可能已经被删除，或链接里的编号不正确。"
          action={
            <Button size="lg" variant="primary" onClick={() => navigate('/')}>
              返回项目列表
            </Button>
          }
        />
      </Card>
    );
  }

  const update = (updater: (draft: Project) => Project) => {
    updateProject(project.id, (current) => updater(current));
  };

  const paramsPanel = <ParamsPanel project={project} onUpdate={update} />;
  const resultsPanel = <ResultPanel assessment={assessment} />;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/"
            className="text-xs text-fg-muted transition-colors hover:text-fg"
          >
            ← 项目列表
          </Link>
          <Badge tone={assessment.verdict.tone} solid={assessment.verdict.level !== 'unknown'}>
            {assessment.verdict.label}
          </Badge>
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div className="min-w-0 flex-1 md:max-w-sm">
            <TextInput
              label="项目名称"
              value={project.name}
              onChange={(event) => update((current) => ({ ...current, name: event.target.value }))}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="lg" variant="default" onClick={() => navigate(`/p/${project.id}/wizard`)}>
              引导填写
            </Button>
            <Button
              size="lg"
              variant="default"
              onClick={() => {
                downloadJson(exportFileName(project), buildExportPayload(project));
                toast.ok('已导出项目 JSON');
              }}
            >
              导出 JSON
            </Button>
            <Button size="lg" variant="primary" onClick={() => navigate(`/p/${project.id}/report`)}>
              评估报告
            </Button>
          </div>
        </div>
      </div>

      {narrow ? (
        <>
          <TabBar
            value={tab}
            onChange={(key) => setTab(key as TabKey)}
            items={[
              { key: 'params', label: '参数' },
              {
                key: 'results',
                label: '结果',
                badge:
                  assessment.missingAll.length > 0 ? (
                    <Badge tone="caution">{assessment.missingAll.length}</Badge>
                  ) : null,
              },
            ]}
          />
          {tab === 'params' ? paramsPanel : resultsPanel}
        </>
      ) : (
        <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
          <div className="flex flex-col gap-3">{paramsPanel}</div>
          <div className="lg:sticky lg:top-16">{resultsPanel}</div>
        </div>
      )}
    </div>
  );
}
